import React from "react";
import { QuoteVerifyInitResponse } from "../types";
import { VERIFY_QUOTE_CARD_CLASS } from "../styles";

const formatRupee = (amount?: number | null) => {
  if (amount === null || amount === undefined) return "—";
  return `₹ ${new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(amount)}`;
};

const formatForeignAmount = (currency?: string, amount?: number | null) => {
  if (amount === null || amount === undefined) return "—";
  return `${currency ?? ""} ${new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)}`;
};

const formatRate = (rate?: number | null) => {
  if (rate === null || rate === undefined) return "—";
  return `₹ ${new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rate)}`;
};

const pickFee = (quote: QuoteVerifyInitResponse, keys: (keyof QuoteVerifyInitResponse)[]) => {
  for (const key of keys) {
    const value = quote[key];
    if (typeof value === "number") return value;
  }
  return undefined;
};

interface QuoteSummaryProps {
  quote: QuoteVerifyInitResponse;
}

const SummaryRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-center justify-between px-6 py-3 border-b border-[#E8E8E8] bg-white">
    <span className="text-sm text-[#333]">{label}</span>
    <span className="text-sm font-semibold text-[#111]">{value}</span>
  </div>
);

const QuoteSummary: React.FC<QuoteSummaryProps> = ({ quote }) => {
  const gst = pickFee(quote, ["totalTax"]);
  const tcs = pickFee(quote, ["tcs"]);
  const nostro = pickFee(quote, ["nostroChargesNew"]);
  const vendorFee = pickFee(quote, ["hopFees"]);
  const bankFee = pickFee(quote, ["nostroCharges"]);

  return (
    <div className={`${VERIFY_QUOTE_CARD_CLASS} overflow-hidden`}>
      <div className="flex-1 flex flex-col">
        <div className="grid grid-cols-3 gap-4 px-6 py-4 bg-[#EBF5FF] border-b border-[#D9D9D9]">
        <div>
          <p className="text-xs text-[#666] mb-1">Currency</p>
          <p className="text-sm font-bold text-[#111]">{quote.destCurrency || "—"}</p>
        </div>
        <div>
          <p className="text-xs text-[#666] mb-1">Exchange Rate</p>
          <p className="text-sm font-bold text-[#111]">{formatRate(quote.conversionRate)}</p>
        </div>
        <div>
          <p className="text-xs text-[#666] mb-1">Purpose of Payment</p>
          <p className="text-sm font-bold text-[#111] leading-snug">
            {quote.transactionPurpose || "—"}
          </p>
        </div>
      </div>

      <SummaryRow label="You Send" value={formatRupee(quote.userSend)} />
      <SummaryRow
        label="They Receive"
        value={formatForeignAmount(quote.destCurrency, quote.recipientRecieve)}
      />
      <SummaryRow label="GST" value={formatRupee(gst)} />
      <SummaryRow label="TCS" value={formatRupee(tcs ?? 0)} />
      <SummaryRow label="NOSTRO" value={formatRupee(nostro)} />
      <SummaryRow label="Vendor Fee" value={formatRupee(vendorFee)} />
      <SummaryRow label="Bank and Convenience Fees" value={formatRupee(bankFee)} />
      </div>

      <div className="mt-auto flex items-center justify-between px-6 py-4 bg-[#EBF5FF]">
        <span className="text-sm font-bold text-[#111]">You Pay</span>
        <span className="text-sm font-bold text-[#111]">{formatRupee(quote.totalPay)}</span>
      </div>
    </div>
  );
};

export default QuoteSummary;
